import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import ProBadge from '@/components/ProBadge';
import { CreditCard, User, Bell, Shield, UserX, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import SubscriptionBanner from '@/components/SubscriptionBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { auth, logout } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [privacySettings, setPrivacySettings] = useState({
    profile_visibility: 'friends',
    allow_friend_requests: true
  });
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  useEffect(() => {
    loadSubscriptionStatus();
    loadPrivacySettings();
    loadBlockedUsers();
    
    // Check if returning from Stripe checkout
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    
    if (sessionId && paymentStatus === 'success') {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled');
      // Clear URL params
      setSearchParams({});
    }
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setSubscriptionStatus(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load subscription status');
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      setProcessingPayment(false);
      setSearchParams({});
      return;
    }
    
    setProcessingPayment(true);
    
    try {
      const response = await axios.get(`${API}/subscription/checkout-status/${sessionId}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        setProcessingPayment(false);
        setSearchParams({});
        loadSubscriptionStatus();
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setProcessingPayment(false);
        setSearchParams({});
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error verifying payment. Please contact support.');
      setProcessingPayment(false);
      setSearchParams({});
    }
  };

  const loadPrivacySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/privacy`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setPrivacySettings(response.data);
    } catch (error) {
      console.error('Failed to load privacy settings');
    }
  };

  const loadBlockedUsers = async () => {
    try {
      const response = await axios.get(`${API}/blocks`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setBlockedUsers(response.data);
    } catch (error) {
      console.error('Failed to load blocked users');
    }
  };

  const handleUpdatePrivacy = async () => {
    try {
      await axios.put(
        `${API}/settings/privacy`,
        privacySettings,
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      toast.success('Privacy settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleUnblock = async (userId) => {
    try {
      await axios.delete(`${API}/blocks/${userId}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      toast.success('User unblocked');
      loadBlockedUsers();
    } catch (error) {
      toast.error('Failed to unblock user');
    }
  };

  const handleSubscribe = async () => {
    try {
      setProcessingPayment(true);
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${API}/subscription/create-checkout`,
        { plan_type: 'monthly', origin_url: originUrl },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will move to the Free plan (20 tasks/week, no Schedule Health or Life Intelligence).')) {
      return;
    }
    
    try {
      setCancellingSubscription(true);
      await axios.post(
        `${API}/subscription/cancel`,
        {},
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      toast.success('Subscription cancelled successfully');
      loadSubscriptionStatus();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setCancellingSubscription(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-bg">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg">
      <SubscriptionBanner />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl heading-font font-bold tracking-tight mb-2 text-white" data-testid="settings-title">
            Settings
          </h1>
          <p className="text-gray-400 body-font">Manage your account and preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Profile */}
          <div className="dark-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <User size={24} className="text-teal-400" />
              <h2 className="text-2xl heading-font font-bold text-white" data-testid="profile-section">Profile</h2>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-gray-300">Name</Label>
                <Input value={auth.user.name} disabled className="mt-2 bg-white/5 border-white/10 text-gray-400" />
              </div>
              <div>
                <Label className="text-gray-300">Email</Label>
                <Input value={auth.user.email} disabled className="mt-2 bg-white/5 border-white/10 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Privacy Settings */}
          <div className="dark-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <Shield size={24} className="text-purple-400" />
              <h2 className="text-2xl heading-font font-bold text-white" data-testid="privacy-section">Privacy</h2>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-gray-300">Profile Visibility</Label>
                <Select 
                  value={privacySettings.profile_visibility} 
                  onValueChange={(value) => setPrivacySettings({...privacySettings, profile_visibility: value})}
                >
                  <SelectTrigger className="mt-2 bg-white/5 border-white/10 text-white focus:border-teal-400" data-testid="visibility-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0E27] border-white/10">
                    <SelectItem value="public">Public - Anyone can view</SelectItem>
                    <SelectItem value="friends">Friends Only</SelectItem>
                    <SelectItem value="private">Private - Only me</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Controls who can view your profile and scheduled tasks</p>
              </div>

              <div className="flex items-center justify-between py-3 border-t border-white/10">
                <div>
                  <p className="font-semibold body-font text-white">Allow Friend Requests</p>
                  <p className="text-xs text-gray-500">Let others send you friend requests</p>
                </div>
                <input
                  type="checkbox"
                  checked={privacySettings.allow_friend_requests}
                  onChange={(e) => setPrivacySettings({...privacySettings, allow_friend_requests: e.target.checked})}
                  className="h-4 w-4"
                  data-testid="allow-requests-checkbox"
                />
              </div>

              <Button
                onClick={handleUpdatePrivacy}
                className="w-full teal-btn"
                data-testid="save-privacy-btn"
              >
                Save Privacy Settings
              </Button>
            </div>
          </div>

          {/* Subscription */}
          <div className="dark-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <CreditCard size={24} className="text-green-400" />
              <h2 className="text-2xl heading-font font-bold text-white" data-testid="subscription-section">Subscription</h2>
            </div>

            {processingPayment && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
                <span className="ml-3 text-gray-300">Verifying payment...</span>
              </div>
            )}

            {!processingPayment && (
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-gray-400 body-font">Status</span>
                  <span className={`font-semibold capitalize body-font flex items-center gap-2 ${
                    subscriptionStatus?.status === 'active' ? 'text-green-400' : 
                    subscriptionStatus?.status === 'cancelled' ? 'text-red-400' : 'text-white'
                  }`} data-testid="subscription-status">
                    {subscriptionStatus?.status === 'active' && <CheckCircle size={16} />}
                    {subscriptionStatus?.status === 'cancelled' && <XCircle size={16} />}
                    {subscriptionStatus?.status === 'trial' && !subscriptionStatus?.is_trial_active ? 'Trial ended' : subscriptionStatus?.status}
                  </span>
                </div>

                {subscriptionStatus?.is_trial_active && subscriptionStatus?.status === 'trial' && (
                  <div className="flex items-center justify-between py-3 border-b border-white/10">
                    <span className="text-gray-400 body-font">Trial Days Remaining</span>
                    <span className="font-semibold body-font text-teal-400" data-testid="trial-days">
                      {subscriptionStatus.days_remaining} days
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-gray-400 body-font">Plan</span>
                  <span className={`font-semibold body-font ${subscriptionStatus?.plan === 'pro' ? 'text-amber-300' : 'text-white'}`} data-testid="current-plan">
                    {subscriptionStatus?.plan === 'pro' ? `Pro${subscriptionStatus?.plan_source === 'trial' ? ' (trial)' : ''}` : 'Free'}
                  </span>
                </div>
                {subscriptionStatus?.plan === 'free' && (
                  <div className="flex items-center justify-between py-3 border-b border-white/10">
                    <span className="text-gray-400 body-font">Tasks this week</span>
                    <span className="font-semibold body-font text-white" data-testid="tasks-usage">
                      {subscriptionStatus.tasks_this_week} / {subscriptionStatus.task_limit}
                    </span>
                  </div>
                )}

                {/* Plans - shown to anyone not on paid Pro */}
                {subscriptionStatus && !['paid', 'demo'].includes(subscriptionStatus.plan_source) && (
                  <div className="pt-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3" data-testid="plan-comparison">
                      <div className="rounded-xl p-4 border border-white/10 bg-white/[0.02]" data-testid="free-plan-card">
                        <h3 className="font-semibold text-white mb-1">Free</h3>
                        <p className="text-2xl font-bold text-white mb-3">$0</p>
                        <ul className="text-xs text-gray-400 space-y-1">
                          <li>• 20 tasks per week</li>
                          <li>• AI smart scheduling</li>
                          <li>• AI chat, trips & suggestions</li>
                        </ul>
                      </div>
                      <div className="rounded-xl p-4 border border-amber-400/40 bg-amber-500/10" data-testid="pro-plan-card">
                        <h3 className="font-semibold text-amber-300 mb-1 flex items-center gap-2">Pro <ProBadge testId="settings-pro-badge"/></h3>
                        <p className="text-2xl font-bold text-white mb-3">$5<span className="text-sm font-normal text-gray-400">/month</span></p>
                        <ul className="text-xs text-gray-300 space-y-1">
                          <li>• Unlimited tasks</li>
                          <li>• Non-Negotiables</li>
                          <li>• Schedule Health</li>
                          <li>• Life Intelligence reports</li>
                        </ul>
                      </div>
                    </div>
                    <Button
                      onClick={handleSubscribe}
                      disabled={processingPayment}
                      className="w-full teal-btn"
                      data-testid="subscribe-btn"
                    >
                      {processingPayment ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        'Upgrade to Pro · $5/month'
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 text-center body-font">
                      Secure payment via Stripe. Cancel anytime.
                    </p>
                  </div>
                )}

                {/* Active Subscription */}
                {subscriptionStatus?.plan_source === 'paid' && (
                  <div className="pt-4 space-y-4">
                    <div className="text-center py-4 bg-green-500/10 border border-green-400/30 rounded-xl">
                      <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                      <p className="text-green-400 font-semibold body-font">Active Subscription</p>
                      <p className="text-sm text-gray-400 mt-1 body-font">Thank you for subscribing!</p>
                    </div>
                    <Button
                      onClick={handleCancelSubscription}
                      disabled={cancellingSubscription}
                      variant="outline"
                      className="w-full border-red-400/30 text-red-400 hover:bg-red-500/10 hover:border-red-400"
                      data-testid="cancel-subscription-btn"
                    >
                      {cancellingSubscription ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Cancelling...
                        </>
                      ) : (
                        'Cancel Subscription'
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Blocked Users */}
          <div className="dark-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <UserX size={24} className="text-red-400" />
              <h2 className="text-2xl heading-font font-bold text-white" data-testid="blocked-users-section">Blocked Users</h2>
            </div>

            {blockedUsers.length === 0 ? (
              <p className="text-center text-gray-400 py-4 body-font" data-testid="no-blocked-users">
                No blocked users
              </p>
            ) : (
              <div className="space-y-2">
                {blockedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 border border-white/10 rounded-xl"
                    data-testid={`blocked-user-${user.id}`}
                  >
                    <div>
                      <p className="font-semibold body-font text-white">{user.name}</p>
                      <p className="text-xs text-gray-500 body-font">{user.email}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-white/10 text-gray-400 hover:text-white hover:border-white/20"
                      onClick={() => handleUnblock(user.id)}
                      data-testid={`unblock-${user.id}`}
                    >
                      Unblock
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Account Actions */}
          <div className="dark-card p-6 lg:col-span-2">
            <h2 className="text-2xl heading-font font-bold mb-6 text-white">Account Actions</h2>
            <Button
              onClick={logout}
              variant="outline"
              className="w-full border-red-400/30 text-red-400 hover:bg-red-500/10 hover:border-red-400"
              data-testid="logout-btn"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;